/**
 * Helpers for binding Yealink call-control to the correct headset.
 *
 * When more than one Yealink is connected, matching a HID interface on vendor id alone
 * binds call control to whichever Yealink enumerated first - often not the one the user
 * selected. These helpers pick the HID interface that belongs to the selected audio device,
 * matched on its USB product id.
 */

/** Yealink USB vendor id */
export const YEALINK_VENDOR_ID = 0x6993

/** HID usage page / usage of the telephony collection that carries call control (button events and hook/ring reports) */
const TELEPHONY_USAGE_PAGE = 0x000b
const TELEPHONY_HEADSET_USAGE = 0x0005

/** Minimal shape of a HIDDevice needed to match Yealink interfaces (kept loose for testability) */
export interface YealinkHidLike {
	vendorId: number
	productId: number
	productName?: string
	/** Top level collections of the HID interface - absent for candidates that are not HID interfaces (the SDK reports devices) */
	collections?: { usagePage?: number, usage?: number }[]
}

/**
 * Does this HID interface expose the telephony collection call control lives on.
 * @param device HID interface to check
 * @returns True when the interface carries the telephony headset collection
 */
function hasCallControlCollection(device: YealinkHidLike) {
	return device.collections?.some(collection => collection.usagePage === TELEPHONY_USAGE_PAGE && collection.usage === TELEPHONY_HEADSET_USAGE) ?? false
}

/**
 * Parse the numeric USB product id out of a MediaDevice.productId string like "6993:0410".
 * @param productId Product id string in "vvvv:pppp" hex form, or undefined
 * @returns The numeric product id, or undefined if it cannot be parsed
 */
export function parseUsbProductId(productId?: string): number | undefined {
	if (!productId) { return undefined }

	const match = /^([0-9a-fA-F]{4}):([0-9a-fA-F]{4})$/.exec(productId)
	if (!match) { return undefined }

	return parseInt(match[2]!, 16) // eslint-disable-line @typescript-eslint/no-non-null-assertion
}

/**
 * Check if an audio device is a Yealink headset
 * @param device Audio device to check (shape kept loose for testability)
 * @returns True if it is a Yealink headset
 */
export function isYealinkMediaDevice(device: { productId?: string, label: string }) {
	return device.productId?.startsWith('6993:') || device.label.toLowerCase().includes('yealink') // productId only works in Chrome
}

/**
 * Pick the Yealink HID interface that belongs to the selected audio device.
 *
 * A single headset is granted as several HID interfaces sharing one vendor/product id - the
 * telephony interface call control lives on, plus vendor ones including a "HID debug"
 * interface. They can all carry the same productName, so interfaces are told apart by their
 * collections and the telephony one always wins; binding to any other silently swallows every
 * sendReport and never delivers a button event. Candidates without collections (the SDK
 * reports devices, not HID interfaces) keep their original order.
 *
 * When the selected device's product id is known, an exact match is required and no arbitrary
 * fallback is made (so control is never bound to the wrong headset); the caller can then
 * request access to the specific device. When the product id is unknown, the first Yealink is
 * returned as a best effort.
 * @param devices HID devices currently granted to the page
 * @param wantedProductId USB product id of the selected audio device, or undefined if unknown
 */
export function findMatchingYealinkHid<T extends YealinkHidLike>(devices: T[], wantedProductId?: number): T | undefined {
	const yealinks = devices.filter(device => device.vendorId === YEALINK_VENDOR_ID && !device.productName?.toLowerCase().includes('debug'))
	if (yealinks.length === 0) { return undefined }

	const candidates = wantedProductId != null ? yealinks.filter(device => device.productId === wantedProductId) : yealinks

	return candidates.find(hasCallControlCollection) ?? candidates[0]
}
